import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const CDN = "https://img.b112j.com/bj/h5/assets";
const icon = (name) => `${CDN}/v3/images/icon-set/menu-type/active/icon-${name}.png`;

// Tab pill: icon + label both inside the pill.
// Each tab may carry:
//   - `to`  → renders as <Link> and navigates on click (active state from URL)
//   - else  → renders as <button> and calls onChange(id) (active state from prop)
//   - `requiresAuth` → if user is not logged in, redirects to /login on click
export const TabPills = ({ tabs, active, onChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const pillStyle = (isActive) => ({
    background: isActive
      ? "linear-gradient(180deg, #19a55c 0%, #0f7a40 100%)"
      : "linear-gradient(180deg, #2a3340 0%, #1a2030 100%)",
    boxShadow: isActive
      ? "0 8px 20px rgba(25,165,92,0.4), inset 0 1px 0 rgba(255,255,255,0.15)"
      : "inset 0 1px 0 rgba(255,255,255,0.05), 0 3px 8px rgba(0,0,0,0.3)",
  });

  const pillCls = (isActive) =>
    `snap-start shrink-0 flex flex-col items-center justify-center min-w-[76px] sm:min-w-[110px] h-[72px] sm:h-[88px] rounded-xl transition-all ${
      isActive ? "shadow-lg" : ""
    }`;

  const pillInner = (t, isActive) => (
    <>
      {t.iconNode ? (
        <span className="h-9 sm:h-12 flex items-center justify-center pointer-events-none drop-shadow-[0_3px_6px_rgba(0,0,0,0.5)] mb-1">
          {t.iconNode}
        </span>
      ) : (
        <img
          src={t.icon}
          alt={t.label}
          className="h-9 sm:h-12 w-auto object-contain pointer-events-none drop-shadow-[0_3px_6px_rgba(0,0,0,0.5)] mb-1"
          loading="lazy"
        />
      )}
      <span
        className={`text-[11px] sm:text-sm font-bold ${
          isActive ? "text-white" : "text-white/80"
        }`}
      >
        {t.label}
      </span>
    </>
  );

  const handleAuthGuard = (t, e) => {
    if (t.requiresAuth && !user) {
      e.preventDefault();
      toast.info("Please log in to view your favourites");
      navigate("/login");
      return true;
    }
    return false;
  };

  return (
    <div
      data-testid="tab-pills"
      className="flex gap-2 sm:gap-2.5 overflow-x-auto py-3 px-3 sm:px-0 scrollbar-thin -mx-3 sm:mx-0 snap-x"
    >
      {tabs.map((t) => {
        const isRouteActive =
          t.to && (t.to === "/" ? location.pathname === "/" : location.pathname.startsWith(t.to));
        const isActive = t.to ? isRouteActive : active === t.id;

        if (t.to) {
          return (
            <Link
              key={t.id}
              to={t.to}
              onClick={(e) => handleAuthGuard(t, e)}
              data-testid={`pill-${t.id}`}
              className={pillCls(isActive)}
              style={pillStyle(isActive)}
            >
              {pillInner(t, isActive)}
            </Link>
          );
        }
        return (
          <button
            key={t.id}
            type="button"
            onClick={(e) => {
              if (handleAuthGuard(t, e)) return;
              onChange && onChange(t.id);
            }}
            data-testid={`pill-${t.id}`}
            className={pillCls(isActive)}
            style={pillStyle(isActive)}
          >
            {pillInner(t, isActive)}
          </button>
        );
      })}
    </div>
  );
};

export const HOME_TABS = [
  { id: "popular", label: "Popular", icon: icon("logo"), to: "/" },
  { id: "sports", label: "Sports", icon: icon("sport"), to: "/sports" },
  { id: "casino", label: "Casino", icon: icon("casino"), to: "/casino" },
  { id: "slots", label: "Slots", icon: icon("slot"), to: "/slots" },
  { id: "crash", label: "Crash", icon: icon("crash"), to: "/crash" },
  { id: "table", label: "Table", icon: icon("table"), to: "/table" },
  { id: "fishing", label: "Fishing", icon: icon("fish"), to: "/fishing" },
  {
    id: "favourite",
    label: "Favourite",
    iconNode: (
      <Heart
        size={36}
        fill="#c89b3c"
        stroke="#c89b3c"
        strokeWidth={1.5}
      />
    ),
    to: "/favourite",
    requiresAuth: true,
  },
  { id: "lottery", label: "Lottery", icon: icon("lottery"), to: "/lottery" },
];
